// CRM-NANCOZI — Rapports & Statistiques
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  FileSpreadsheet, FileText,
  TrendingUp, Users, Briefcase, RefreshCw,
  BarChart2, PieChart, Award, Target, Edit2, Check, X,
} from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

/* ── Constantes ── */
const DOMAINES = { action: 'ACTION', obligation: 'OBLIGATION', autre: 'AUTRE' };
const SGI_LABELS = {
  sgi_bs: 'SGI BS', sgi_pcm: 'SGI PCM', nsia_finance: 'NSIA FINANCE',
  sgi_bici_bourse: 'SGI BICI BOURSE', societe_generale: 'SOCIETE GENERALE',
  atlantique_finance: 'ATLANTIQUE FINANCE', boa_capita_securities: 'BOA CAPITAL SECURITIES', autre: 'Autre',
};
const PAIEMENT_LABELS = { paye: 'Payé', partiel: 'Partiel', impaye: 'Impayé' };
const STATUT_LABELS   = { en_cours: 'En cours', termine: 'Terminé', resilie: 'Résilié', suspendu: 'Suspendu' };

const COLORS = ['#1F4E79','#2E75B6','#1E7145','#ED7D31','#C00000','#6A0DAD','#00838F','#F59E0B'];
const fmtN   = (n) => Number(n || 0).toLocaleString('fr-FR');

const tooltipOpts = { backgroundColor: '#1F2937', padding: 10, cornerRadius: 8 };
const legendOpts  = { position: 'bottom', labels: { padding: 12, font: { size: 10 }, usePointStyle: true } };

const optionsBar = {
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: legendOpts, tooltip: tooltipOpts,
    datalabels: { display: false },
  },
  scales: {
    y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 }, callback: v => Number(v).toLocaleString('fr-FR') } },
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
  },
};
const optionsDoughnut = {
  responsive: true, maintainAspectRatio: false, cutout: '60%',
  plugins: {
    legend: legendOpts,
    tooltip: {
      ...tooltipOpts,
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct   = total > 0 ? Math.round((ctx.parsed / total) * 100) : 0;
          return ` ${ctx.label} : ${ctx.parsed} (${pct}%)`;
        },
      },
    },
    datalabels: {
      color: '#fff',
      font: { size: 11, weight: 700 },
      formatter: (value, ctx) => {
        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
        const pct   = total > 0 ? Math.round((value / total) * 100) : 0;
        return pct >= 5 ? `${pct}%` : '';
      },
    },
  },
};

const TYPE_CONFIG = {
  coaching:       { label: 'Coaching',       icon: Briefcase, color: '#1F4E79', bg: '#EBF3FB' },
  renouvellement: { label: 'Renouvellement', icon: RefreshCw, color: '#ED7D31', bg: '#FFF3E0' },
  formation:      { label: 'Formation',      icon: Users,     color: '#1E7145', bg: '#E8F5E9' },
};

/* ── Styles compacts ── */
const S = {
  head:   { display:'flex', alignItems:'center', gap:7, paddingBottom:8,
            borderBottom:'1px solid #E5E7EB', marginBottom:10 },
  htitle: { fontSize:11, fontWeight:700, color:'#1F4E79', margin:0,
            textTransform:'uppercase', letterSpacing:'0.05em' },
};

function SHead({ icon: Icon, title, color = '#1F4E79', right }) {
  return (
    <div style={{ ...S.head, justifyContent:'space-between' }}>
      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
        <Icon size={13} color={color}/>
        <span style={{ ...S.htitle, color }}>{title}</span>
      </div>
      {right}
    </div>
  );
}

/* ── KPI Card ── */
function KPI({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="card" style={{ padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <div>
        <div style={{ fontSize:10, color:'#6B7280', marginBottom:3 }}>{label}</div>
        <div style={{ fontSize:18, fontWeight:800, color:'#1F2937' }}>{value}</div>
        {sub && <div style={{ fontSize:10, color:'#9CA3AF', marginTop:2 }}>{sub}</div>}
      </div>
      <div style={{ background: color+'18', borderRadius:8, padding:8 }}>
        <Icon size={18} color={color}/>
      </div>
    </div>
  );
}

function pct(realise, objectif) {
  if (!objectif || objectif === 0) return null;
  return Math.round((realise / objectif) * 100);
}
function PctBadge({ value }) {
  if (value === null) return <span style={{ color:'#9CA3AF', fontSize:11 }}>—</span>;
  const color = value >= 100 ? '#1E7145' : value >= 75 ? '#ED7D31' : '#C00000';
  const bg    = value >= 100 ? '#D1FAE5' : value >= 75 ? '#FEF3C7' : '#FEE2E2';
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:99, color, background:bg }}>
      {value}%
    </span>
  );
}
function ProgressBar({ value, color }) {
  const w = Math.min(100, value || 0);
  const c = (value || 0) >= 100 ? '#1E7145' : (value || 0) >= 75 ? '#ED7D31' : '#C00000';
  return (
    <div style={{ height:6, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
      <div style={{ width:`${w}%`, height:'100%', background:color||c, borderRadius:4, transition:'width 0.5s ease' }}/>
    </div>
  );
}

const ANNEE_COURANTE = new Date().getFullYear();

function NoData() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#9CA3AF', fontSize:12 }}>
      Pas de données
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Page principale
══════════════════════════════════════════════════ */
export default function RapportsPage() {
  const { user } = useSelector(s => s.auth);
  const isAdmin  = user?.role === 'administrateur';

  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [exporting, setExporting] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin,   setDateFin]   = useState('');
  const [typeExport] = useState('clients');

  const [objAnnee,  setObjAnnee]  = useState(ANNEE_COURANTE);
  const [objType,   setObjType]   = useState('coaching');
  const [objByType, setObjByType] = useState({});
  const [editObj,   setEditObj]   = useState(false);
  const [objForm,   setObjForm]   = useState({});
  const [savingObj, setSavingObj] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin)   params.date_fin   = dateFin;
      const { data } = await api.get('/dashboard/statistiques/', { params });
      setStats(data);
    } catch { toast.error('Erreur chargement des statistiques.'); }
    finally { setLoading(false); }
  }, [dateDebut, dateFin]);

  const loadObjectifs = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/objectifs/', { params: { annee: objAnnee } });
      const map = {};
      for (const obj of data) map[obj.type_activite] = obj;
      setObjByType(map);
    } catch { /* silent */ }
  }, [objAnnee]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadObjectifs(); }, [loadObjectifs]);

  const handleTypeChange = (type) => {
    setObjType(type);
    setObjForm(objByType[type] || {});
    setEditObj(false);
  };

  const handleSaveObjectif = async (e) => {
    e.preventDefault();
    setSavingObj(true);
    try {
      const resp = await api.post('/dashboard/objectifs/upsert/', {
        ...objForm, annee: objAnnee, type_activite: objType,
      });
      setObjByType(prev => ({ ...prev, [objType]: resp.data }));
      setEditObj(false);
      toast.success('Objectif enregistré !');
    } catch { toast.error("Erreur lors de l'enregistrement."); }
    finally { setSavingObj(false); }
  };

  const getRealizedForType = (type) => {
    if (!stats) return { ca: 0, nb: 0, taux: 0, ca_monthly: [] };
    const { kpis, ca_mensuel = [] } = stats;
    switch (type) {
      case 'coaching':
        return { ca: Number(kpis.ca_coachings || 0), nb: (kpis.nb_coachings||0)-(kpis.nb_renouvellements||0), ca_monthly: ca_mensuel.map(m => m.coachings||0) };
      case 'renouvellement':
        return { ca: Number(kpis.ca_renouvellements||0), nb: kpis.nb_renouvellements||0, taux: kpis.taux_renouvellement||0, ca_monthly: ca_mensuel.map(m => m.renouvellements||0) };
      case 'formation':
        return { ca: Number(kpis.ca_formations||0), nb: kpis.nb_formations||0, ca_monthly: ca_mensuel.map(m => m.formations||0) };
      default:
        return { ca: 0, nb: 0, ca_monthly: [] };
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const resp = await api.get('/dashboard/export/', {
        params: { format, type: typeExport }, responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `rapport_${typeExport}_${new Date().toISOString().slice(0,10)}.${format==='pdf'?'pdf':'xlsx'}`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Rapport ${format.toUpperCase()} téléchargé !`);
    } catch { toast.error("Erreur lors de l'export."); }
    finally { setExporting(''); }
  };

  /* ── Données charts ── */
  const chartCAmensuel = stats ? {
    labels: stats.ca_mensuel.map(m => m.mois),
    datasets: [
      { label:'Coachings',       data: stats.ca_mensuel.map(m => m.coachings),           backgroundColor:'rgba(31,78,121,0.85)',  borderColor:'#1F4E79', borderWidth:1.5, borderRadius:4 },
      { label:'Formations',      data: stats.ca_mensuel.map(m => m.formations),           backgroundColor:'rgba(30,113,69,0.85)',  borderColor:'#1E7145', borderWidth:1.5, borderRadius:4 },
      { label:'Renouvellements', data: stats.ca_mensuel.map(m => m.renouvellements||0),   backgroundColor:'rgba(237,125,49,0.85)', borderColor:'#ED7D31', borderWidth:1.5, borderRadius:4 },
    ],
  } : null;

  const chartDomaines = stats?.par_domaine?.length ? {
    labels: stats.par_domaine.map(d => DOMAINES[d.domaine] || d.domaine || 'N/A'),
    datasets: [{ data: stats.par_domaine.map(d => d.nb), backgroundColor: COLORS, borderWidth:3, borderColor:'white', hoverOffset:6 }],
  } : null;

  const chartSGI = stats?.par_sgi?.length ? {
    labels: stats.par_sgi.map(d => SGI_LABELS[d.sgi] || d.sgi || 'N/A'),
    datasets: [{ data: stats.par_sgi.map(d => d.nb), backgroundColor: COLORS, borderWidth:3, borderColor:'white', hoverOffset:6 }],
  } : null;

  const chartPaiement = stats?.par_paiement?.length ? {
    labels: stats.par_paiement.map(d => PAIEMENT_LABELS[d.statut_paiement] || d.statut_paiement),
    datasets: [{ label:'Nb coachings', data: stats.par_paiement.map(d => d.nb), backgroundColor:['#1E7145','#ED7D31','#C00000'], borderRadius:5, borderWidth:0 }],
  } : null;

  const chartStatut = stats?.par_statut?.length ? {
    labels: stats.par_statut.map(d => STATUT_LABELS[d.statut] || d.statut),
    datasets: [{ label:'Coachings', data: stats.par_statut.map(d => d.nb), backgroundColor:['#2E75B6','#1E7145','#C00000','#ED7D31'], borderRadius:5, borderWidth:0 }],
  } : null;

  const { kpis } = stats || {};

  const optBarCA = {
    ...optionsBar,
    plugins: {
      ...optionsBar.plugins,
      tooltip: { ...tooltipOpts, callbacks: { label: ctx => ` ${ctx.dataset.label} : ${Number(ctx.parsed.y).toLocaleString('fr-FR')} FCFA` } },
    },
    scales: {
      ...optionsBar.scales,
      y: { ...optionsBar.scales?.y, ticks: { font:{ size:10 }, callback: v => Number(v).toLocaleString('fr-FR') } },
    },
  };

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* ── Barre d'outils ── */}
      <div style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
        paddingBottom:10, marginBottom:10, borderBottom:'1px solid #E5E7EB' }}>
        <button className="header-btn btn-secondary btn-sm" onClick={loadStats} disabled={loading}
          style={{ display:'flex', alignItems:'center', gap:5 }}>
          <RefreshCw size={13}/> Actualiser
        </button>
        <div style={{ width:1, height:22, background:'#E5E7EB' }}/>
        <span style={{ fontSize:11, color:'#6B7280', fontWeight:600 }}>Période :</span>
        <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
          style={{ height:32, padding:'0 8px', fontSize:12, border:'1px solid #D1D5DB', borderRadius:7, color:'#1F2937' }}/>
        <span style={{ fontSize:11, color:'#9CA3AF' }}>→</span>
        <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
          style={{ height:32, padding:'0 8px', fontSize:12, border:'1px solid #D1D5DB', borderRadius:7, color:'#1F2937' }}/>
        {(dateDebut || dateFin) && (
          <button onClick={() => { setDateDebut(''); setDateFin(''); }}
            style={{ fontSize:11, color:'#C00000', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
            Tout afficher
          </button>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
          <button className="header-btn btn-secondary btn-sm" onClick={() => handleExport('excel')}
            disabled={!!exporting} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <FileSpreadsheet size={13}/> {exporting==='excel' ? 'Export…' : 'Excel'}
          </button>
          <button className="header-btn btn-secondary btn-sm" onClick={() => handleExport('pdf')}
            disabled={!!exporting} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <FileText size={13}/> {exporting==='pdf' ? 'Export…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* ── Contenu scrollable ── */}
      {loading ? (
        <div className="spinner" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div className="spinner-ring"/> Chargement des statistiques…
        </div>
      ) : stats && (
        <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:12 }}>

          {/* ── KPIs ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
            <KPI label="CA Total (FCFA)"           value={fmtN(kpis.ca_total)}          sub="Coaching + Formation + Renouvellement"  icon={TrendingUp} color="#1F4E79"/>
            <KPI label="CA Coachings (FCFA)"        value={fmtN(kpis.ca_coachings)}      sub={`${fmtN(kpis.nb_coachings-kpis.nb_renouvellements)} contrats 1ers`} icon={Briefcase} color="#2E75B6"/>
            <KPI label="CA Formations (FCFA)"       value={fmtN(kpis.ca_formations)}     sub={`${fmtN(kpis.nb_formations)} inscription(s)`}                        icon={Users}     color="#1E7145"/>
            <KPI label="CA Renouvellements (FCFA)"  value={fmtN(kpis.ca_renouvellements)} sub={`${kpis.nb_renouvellements} renouvellement(s) — Taux ${kpis.taux_renouvellement}%`} icon={RefreshCw} color="#ED7D31"/>
          </div>

          {/* ── Grille principale : Gauche 3fr | Droite 2fr ── */}
          <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:12, alignItems:'start' }}>

            {/* ── Col gauche ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

              {/* CA mensuel chart */}
              <div className="card" style={{ padding:'12px 14px' }}>
                <SHead icon={BarChart2} title="Chiffre d'affaires mensuel"
                  right={<span style={{ fontSize:10, color:'#9CA3AF' }}>Coaching + Formation + Renouvellement — {new Date().getFullYear()}</span>}/>
                <div style={{ position:'relative', height:220 }}>
                  {chartCAmensuel ? <Bar data={chartCAmensuel} options={optBarCA}/> : <NoData/>}
                </div>
              </div>

              {/* Tableau CA détail mensuel */}
              <div className="card" style={{ padding:'12px 14px' }}>
                <SHead icon={BarChart2} title="Détail mensuel CA"
                  right={<span style={{ fontSize:10, color:'#9CA3AF' }}>En FCFA — {new Date().getFullYear()}</span>}/>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', tableLayout:'fixed' }}>
                    <colgroup>
                      <col style={{ width:'16%' }}/>
                      <col style={{ width:'17%' }}/>
                      <col style={{ width:'17%' }}/>
                      <col style={{ width:'20%' }}/>
                      <col style={{ width:'16%' }}/>
                      <col style={{ width:'14%' }}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ fontSize:10 }}>Mois</th>
                        <th style={{ fontSize:10, color:'#1F4E79' }}>Coaching</th>
                        <th style={{ fontSize:10, color:'#1E7145' }}>Formation</th>
                        <th style={{ fontSize:10, color:'#ED7D31' }}>Renouvellement</th>
                        <th style={{ fontSize:10, color:'#1F2937' }}>Total</th>
                        <th style={{ fontSize:10 }}>Part</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const grandTotal = stats.ca_mensuel.reduce((s, m) => s+(m.total||0), 0);
                        return stats.ca_mensuel.map((m, i) => {
                          const total = m.total || 0;
                          const part  = grandTotal > 0 ? Math.round((total/grandTotal)*100) : 0;
                          const isCur = i === new Date().getMonth();
                          return (
                            <tr key={m.mois} style={{ background:isCur?'#EBF3FB':undefined, fontWeight:isCur?700:400 }}>
                              <td style={{ fontSize:11, padding:'5px 8px' }}>
                                {m.mois}
                                {isCur && <span style={{ fontSize:8, background:'#1F4E79', color:'#fff', borderRadius:3, padding:'1px 4px', marginLeft:4 }}>Actuel</span>}
                              </td>
                              <td style={{ fontSize:11, padding:'5px 8px', color:m.coachings>0?'#1F4E79':'#9CA3AF' }}>{m.coachings>0?fmtN(m.coachings):'—'}</td>
                              <td style={{ fontSize:11, padding:'5px 8px', color:m.formations>0?'#1E7145':'#9CA3AF' }}>{m.formations>0?fmtN(m.formations):'—'}</td>
                              <td style={{ fontSize:11, padding:'5px 8px', color:(m.renouvellements||0)>0?'#ED7D31':'#9CA3AF' }}>{(m.renouvellements||0)>0?fmtN(m.renouvellements):'—'}</td>
                              <td style={{ fontSize:11, padding:'5px 8px', fontWeight:700, color:total>0?'#1F2937':'#9CA3AF' }}>{total>0?fmtN(total):'—'}</td>
                              <td style={{ padding:'5px 8px' }}>
                                {part > 0 ? (
                                  <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                                    <div style={{ flex:1, height:5, background:'#F3F4F6', borderRadius:3, overflow:'hidden' }}>
                                      <div style={{ width:`${part}%`, height:'100%', background:'#1F4E79', borderRadius:3 }}/>
                                    </div>
                                    <span style={{ fontSize:9, color:'#6B7280', minWidth:24, textAlign:'right' }}>{part}%</span>
                                  </div>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#1F4E79', color:'white' }}>
                        <td style={{ fontSize:11, fontWeight:700, padding:'6px 8px' }}>TOTAL</td>
                        <td style={{ fontSize:11, fontWeight:700, padding:'6px 8px' }}>{fmtN(stats.ca_mensuel.reduce((s,m)=>s+m.coachings,0))}</td>
                        <td style={{ fontSize:11, fontWeight:700, padding:'6px 8px' }}>{fmtN(stats.ca_mensuel.reduce((s,m)=>s+m.formations,0))}</td>
                        <td style={{ fontSize:11, fontWeight:700, padding:'6px 8px' }}>{fmtN(stats.ca_mensuel.reduce((s,m)=>s+(m.renouvellements||0),0))}</td>
                        <td style={{ fontSize:12, fontWeight:800, padding:'6px 8px' }}>{fmtN(stats.ca_mensuel.reduce((s,m)=>s+(m.total||0),0))}</td>
                        <td style={{ fontSize:11, fontWeight:700, padding:'6px 8px' }}>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Domaines + SGI */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={PieChart} title="Répartition par domaine"/>
                  <div style={{ position:'relative', height:190 }}>
                    {chartDomaines ? <Doughnut data={chartDomaines} options={optionsDoughnut}/> : <NoData/>}
                  </div>
                </div>
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={PieChart} title="Répartition par SGI"/>
                  <div style={{ position:'relative', height:190 }}>
                    {chartSGI ? <Doughnut data={chartSGI} options={optionsDoughnut}/> : <NoData/>}
                  </div>
                </div>
              </div>

              {/* Paiements + Statuts */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={BarChart2} title="Statut des paiements"/>
                  <div style={{ position:'relative', height:190 }}>
                    {chartPaiement ? <Bar data={chartPaiement} options={optionsBar}/> : <NoData/>}
                  </div>
                </div>
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={BarChart2} title="Statut des coachings"/>
                  <div style={{ position:'relative', height:190 }}>
                    {chartStatut ? <Bar data={chartStatut} options={optionsBar}/> : <NoData/>}
                  </div>
                </div>
              </div>

            </div>

            {/* ── Col droite ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

              {/* ── Objectifs ── */}
              {(() => {
                const curObj   = objByType[objType];
                const realized = getRealizedForType(objType);
                const cfg      = TYPE_CONFIG[objType];
                const TypeIcon = cfg.icon;

                const caMonthlyChart = stats?.ca_mensuel?.length && curObj && Number(curObj.ca_mensuel) > 0 ? {
                  labels: stats.ca_mensuel.map(m => m.mois),
                  datasets: [
                    { label:'Objectif', data: stats.ca_mensuel.map(() => Number(curObj.ca_mensuel)), backgroundColor:'rgba(107,114,128,0.22)', borderColor:'#9CA3AF', borderWidth:1.5, borderRadius:3 },
                    { label:'Réalisé',  data: realized.ca_monthly, backgroundColor: realized.ca_monthly.map(v => v>=Number(curObj.ca_mensuel)?'rgba(30,113,69,0.85)':'rgba(192,0,0,0.72)'), borderColor: realized.ca_monthly.map(v => v>=Number(curObj.ca_mensuel)?'#1E7145':'#C00000'), borderWidth:1.5, borderRadius:3 },
                  ],
                } : null;

                return (
                  <div className="card" style={{ padding:'12px 14px', border:`2px solid ${cfg.bg}` }}>

                    {/* Header objectifs */}
                    <div style={{ ...S.head, justifyContent:'space-between' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <Target size={13} color={cfg.color}/>
                        <span style={{ ...S.htitle, color:cfg.color }}>Objectifs</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <input type="number" min="2020" max="2099" value={objAnnee}
                          onChange={e => setObjAnnee(Number(e.target.value))}
                          style={{ width:62, height:26, padding:'0 6px', fontSize:12, fontWeight:700,
                            border:`1.5px solid ${cfg.color}`, borderRadius:6, textAlign:'center',
                            color:cfg.color, outline:'none' }}/>
                        {isAdmin && (
                          <button onClick={() => { setEditObj(!editObj); if (!editObj) setObjForm(curObj||{}); }}
                            className="header-btn btn-secondary btn-sm"
                            style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, padding:'3px 8px' }}>
                            <Edit2 size={11}/> {editObj?'Fermer':curObj?'Modifier':'Définir'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Onglets type */}
                    <div style={{ display:'flex', borderBottom:'1px solid #F3F4F6', marginBottom:10, background:'#FAFAFA', borderRadius:4 }}>
                      {Object.entries(TYPE_CONFIG).map(([key, c]) => {
                        const active = objType === key;
                        const TIcon  = c.icon;
                        return (
                          <button key={key} onClick={() => handleTypeChange(key)} style={{
                            flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:4,
                            padding:'7px 6px', fontSize:10, fontWeight:active?700:500,
                            color:active?c.color:'#6B7280', background:'none', border:'none',
                            borderBottom:active?`2px solid ${c.color}`:'2px solid transparent',
                            cursor:'pointer',
                          }}>
                            <TIcon size={11}/> {c.label}
                            {!!objByType[key] && <span style={{ width:5, height:5, borderRadius:'50%', background:c.color }}/>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Formulaire édit */}
                    {editObj && isAdmin && (
                      <form onSubmit={handleSaveObjectif} style={{ padding:'10px 12px', background:'#F9FAFB', borderRadius:6, border:'1px solid #E5E7EB', marginBottom:10 }}>
                        <div style={{ fontSize:10, fontWeight:600, color:cfg.color, marginBottom:8 }}>
                          Objectifs {cfg.label} — {objAnnee}
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                          {[
                            { field:'nb_annuel',  label:`Nb ${cfg.label}/an`,   type:'number' },
                            { field:'nb_mensuel', label:`Nb ${cfg.label}/mois`, type:'number' },
                            { field:'ca_annuel',  label:'CA annuel (FCFA)',      type:'number' },
                            { field:'ca_mensuel', label:'CA mensuel (FCFA)',     type:'number' },
                            ...(objType==='renouvellement' ? [{ field:'taux_cible', label:'Taux cible (%)', type:'number', step:'0.1' }] : []),
                          ].map(({ field, label, type, step }) => (
                            <div key={field}>
                              <label style={{ display:'block', fontSize:10, fontWeight:600, color:'#374151', marginBottom:2 }}>{label}</label>
                              <input type={type} step={step||'1'} className="form-control"
                                style={{ fontSize:12, padding:'5px 8px' }}
                                value={objForm[field]??0}
                                onChange={e => setObjForm(f => ({ ...f, [field]:e.target.value }))}/>
                            </div>
                          ))}
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:6 }}>
                          <button type="button" className="header-btn btn-secondary btn-sm"
                            onClick={() => { setEditObj(false); setObjForm(curObj||{}); }}
                            style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <X size={11}/> Annuler
                          </button>
                          <button type="submit" className="header-btn btn-primary btn-sm" disabled={savingObj}
                            style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <Check size={11}/> {savingObj?'Enregistrement…':'Enregistrer'}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Contenu objectifs */}
                    {curObj ? (
                      <div>
                        {/* Jauges */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                          {[
                            { label:`CA ${cfg.label}`, realise:realized.ca, obj:Number(curObj.ca_annuel||0), fmt:v=>fmtN(v)+' FCFA', color:cfg.color },
                            { label:`Nb ${cfg.label}`, realise:realized.nb, obj:Number(curObj.nb_annuel||0), fmt:v=>v, color:cfg.color },
                            ...(objType==='renouvellement'?[{ label:'Taux renouvellement', realise:realized.taux||0, obj:Number(curObj.taux_cible||0), fmt:v=>v+'%', color:'#6A0DAD' }]:[]),
                          ].map(({ label, realise, obj:objVal, fmt, color }) => {
                            const p = pct(realise, objVal);
                            return (
                              <div key={label} className="card" style={{ padding:'8px 10px', borderLeft:`3px solid ${color}` }}>
                                <div style={{ fontSize:9, color:'#6B7280', marginBottom:4 }}>{label}</div>
                                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:5 }}>
                                  <span style={{ fontSize:14, fontWeight:800, color:'#1F2937' }}>{fmt(realise)}</span>
                                  <PctBadge value={p}/>
                                </div>
                                <ProgressBar value={p} color={color}/>
                                <div style={{ fontSize:9, color:'#9CA3AF', marginTop:3 }}>Obj : {fmt(objVal)}</div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Tableau suivi mensuel */}
                        <div style={{ marginBottom:10 }}>
                          <div style={{ fontSize:10, fontWeight:700, color:cfg.color, marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
                            <TrendingUp size={11}/> Suivi CA mensuel
                          </div>
                          <table style={{ width:'100%', tableLayout:'fixed', fontSize:11 }}>
                            <thead>
                              <tr>
                                <th style={{ fontSize:9, textAlign:'left' }}>Mois</th>
                                <th style={{ fontSize:9 }}>Obj/mois</th>
                                <th style={{ fontSize:9 }}>Réalisé</th>
                                <th style={{ fontSize:9 }}>%</th>
                                <th style={{ fontSize:9, width:50 }}>Jauge</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(stats?.ca_mensuel||[]).map((m, i) => {
                                const obj_m = Number(curObj.ca_mensuel||0);
                                const val   = realized.ca_monthly[i]||0;
                                const p     = pct(val, obj_m);
                                const isCur = i === new Date().getMonth();
                                return (
                                  <tr key={m.mois} style={{ background:isCur?cfg.bg:undefined, fontWeight:isCur?700:400 }}>
                                    <td style={{ padding:'4px 6px', fontSize:10 }}>
                                      {m.mois}
                                      {isCur && <span style={{ fontSize:7, background:cfg.color, color:'#fff', borderRadius:3, padding:'1px 3px', marginLeft:3 }}>Actuel</span>}
                                    </td>
                                    <td style={{ padding:'4px 6px', textAlign:'center', color:'#6B7280', fontSize:10 }}>{obj_m>0?fmtN(obj_m):'—'}</td>
                                    <td style={{ padding:'4px 6px', textAlign:'center', fontSize:10, color:val>0?'#1F2937':'#9CA3AF', fontWeight:600 }}>{val>0?fmtN(val):'—'}</td>
                                    <td style={{ padding:'4px 6px', textAlign:'center' }}><PctBadge value={p}/></td>
                                    <td style={{ padding:'4px 6px' }}>{p!==null?<ProgressBar value={p} color={cfg.color}/>:'—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Chart CA réalisé vs objectif */}
                        {caMonthlyChart && (
                          <div>
                            <div style={{ fontSize:10, fontWeight:700, color:'#1F2937', marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
                              <BarChart2 size={11} color={cfg.color}/> CA réalisé vs objectif
                            </div>
                            <div style={{ position:'relative', height:180 }}>
                              <Bar data={caMonthlyChart} options={optBarCA}/>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      !editObj && (
                        <div style={{ textAlign:'center', color:'#9CA3AF', padding:'20px 0' }}>
                          <TypeIcon size={28} style={{ marginBottom:6, opacity:0.3 }}/>
                          <p style={{ fontSize:11 }}>
                            {isAdmin
                              ? `Cliquez sur "Définir" pour paramétrer les objectifs ${cfg.label} ${objAnnee}.`
                              : `Aucun objectif ${cfg.label} défini pour ${objAnnee}.`}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                );
              })()}

              {/* ── Top commerciaux ── */}
              {stats.top_commerciaux?.length > 0 && (
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={Award} title="Performance commerciaux" color="#ED7D31"
                    right={<span style={{ fontSize:10, color:'#9CA3AF' }}>Par chiffre d'affaires</span>}/>
                  <table style={{ width:'100%', tableLayout:'fixed' }}>
                    <colgroup>
                      <col style={{ width:30 }}/>
                      <col style={{ width:'38%' }}/>
                      <col style={{ width:52 }}/>
                      <col style={{ width:44 }}/>
                      <col style={{ width:'30%' }}/>
                      <col style={{ width:36 }}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ fontSize:9 }}>Rang</th>
                        <th style={{ fontSize:9 }}>Commercial</th>
                        <th style={{ fontSize:9 }}>Coachs</th>
                        <th style={{ fontSize:9 }}>Clients</th>
                        <th style={{ fontSize:9 }}>CA (FCFA)</th>
                        <th style={{ fontSize:9 }}>Part</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_commerciaux.map((c, i) => {
                        const caTotal = stats.top_commerciaux.reduce((s,x) => s+Number(x.ca||0), 0);
                        const part    = caTotal ? Math.round((Number(c.ca||0)/caTotal)*100) : 0;
                        const medal   = i===0?'🥇':i===1?'🥈':i===2?'🥉':`${i+1}`;
                        return (
                          <tr key={c.commercial}>
                            <td style={{ fontSize:12, textAlign:'center', fontWeight:700 }}>{medal}</td>
                            <td style={{ padding:'6px 4px' }}>
                              <div style={{ fontSize:11, fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                {c.commercial__prenom_util} {c.commercial__nom_util}
                              </div>
                            </td>
                            <td style={{ fontSize:11, textAlign:'center', fontWeight:600, color:'#1F4E79' }}>{c.nb}</td>
                            <td style={{ fontSize:11, textAlign:'center', color:'#1E7145' }}>{c.clients}</td>
                            <td style={{ padding:'6px 4px' }}>
                              <div style={{ fontSize:11, fontWeight:700, marginBottom:2 }}>{fmtN(c.ca)}</div>
                              <div style={{ height:5, background:'#F3F4F6', borderRadius:3, overflow:'hidden' }}>
                                <div style={{ width:`${part}%`, height:'100%', background:'#1F4E79', borderRadius:3 }}/>
                              </div>
                            </td>
                            <td style={{ fontSize:11, textAlign:'center', fontWeight:700, color:'#ED7D31' }}>{part}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Top formations ── */}
              {stats.stats_formations?.length > 0 && (
                <div className="card" style={{ padding:'12px 14px' }}>
                  <SHead icon={Award} title="Top formations" color="#1E7145"
                    right={<span style={{ fontSize:10, color:'#9CA3AF' }}>Par CA généré</span>}/>
                  <table style={{ width:'100%', tableLayout:'fixed' }}>
                    <colgroup>
                      <col style={{ width:24 }}/>
                      <col style={{ width:64 }}/>
                      <col/>
                      <col style={{ width:44 }}/>
                      <col style={{ width:70 }}/>
                      <col style={{ width:80 }}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ fontSize:9 }}>#</th>
                        <th style={{ fontSize:9 }}>Code</th>
                        <th style={{ fontSize:9 }}>Formation</th>
                        <th style={{ fontSize:9 }}>Inscrits</th>
                        <th style={{ fontSize:9 }}>Prix</th>
                        <th style={{ fontSize:9 }}>CA (FCFA)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.stats_formations.map((f, i) => (
                        <tr key={i}>
                          <td style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'#1F4E79' }}>{i+1}</td>
                          <td style={{ fontSize:10, fontFamily:'monospace', color:'#1F4E79', fontWeight:700 }}>{f.code_formation}</td>
                          <td style={{ fontSize:11, padding:'6px 4px', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{f.intitule}</td>
                          <td style={{ textAlign:'center' }}><span className="badge badge-bleu" style={{ fontSize:9 }}>{f.nb_inscrits}</span></td>
                          <td style={{ fontSize:11 }}>{fmtN(f.prix)}</td>
                          <td style={{ fontSize:11, fontWeight:700, color:'#1E7145' }}>{fmtN(f.ca)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          </div>

        </div>
      )}
    </div>
  );
}
